import { AIONZ_DIALOG_DATA, AionzDialogModule, AionzDialogRef, HeaderBottomSheetComponent, AionzTooltip } from '@aionzdev/design-system';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, PLATFORM_ID, inject, output, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { auditTime, Subject } from 'rxjs';
import { WebsocketService } from '@aionzdev/modules';
import { ProductIntegrationStatus } from '../../../enums';
import { ApproveQuotationMapItem } from '../../../interfaces/approve-quotation-map';
import { ProductIntegrationDialogData } from '../../../interfaces/product-integration-dialog-data';
import { ProductIntegrationRetryEvent } from '../../../interfaces/product-integration-retry-event';
import { PurchasingEvent } from '../../../interfaces/purchasing-event';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { KanbanChipBadgeComponent } from '../../atoms/kanban-chip-badge/kanban-chip-badge.component';
import { ProductIntegrationAccordionComponent } from '../../atoms/product-integration-accordion/product-integration-accordion.component';
import { PurchaseRequestStatusComponent } from '../../atoms/purchase-request-status/purchase-request-status.component';


@Component({
  selector: 'aionz-product-integration-dialog',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PurchaseRequestStatusComponent,
    KanbanChipBadgeComponent,
    ProductIntegrationAccordionComponent,
    AionzTooltip,
    AionzDialogModule,
    HeaderBottomSheetComponent
  ],
  templateUrl: './product-integration-dialog.component.html',
  styleUrl: './product-integration-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductIntegrationDialogComponent {
  public data = signal<ProductIntegrationDialogData>(
    inject<ProductIntegrationDialogData>(AIONZ_DIALOG_DATA)
  );
  private dialogRef = inject(AionzDialogRef<ProductIntegrationDialogComponent>);
  private purchasingService = inject(AbstractPurchasingService);
  private websocketService = inject(WebsocketService);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);
  private websocketUpdate$ = new Subject<void>();

  public isLoading = signal<boolean>(false);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.setupRealtimeUpdates();
    }
  }

  private setupRealtimeUpdates(): void {
    const purchaseId = this.data().purchaseId;
    if (!purchaseId) return;

    const config = this.purchasingService.getRealtimeConfig();
    const updateTypes = new Set(config.updateTypes.integration);

    this.websocketUpdate$
      .pipe(auditTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.purchasingService.findUncompletedPurchases(purchaseId).subscribe({
          next: response => {
            if (response.body) {
              this.data.set(response.body);
            }
          }
        });
      });

    this.websocketService.sendMessage(config.joinEvent, config.tenantId);

    this.websocketService.listen(config.eventName, (event: any) => {
      const mappedEvent: PurchasingEvent = this.purchasingService.getPurchasingEvent(event);
      if (
        String(mappedEvent.tenantId) === String(config.tenantId) &&
        mappedEvent.purchaseId === purchaseId &&
        updateTypes.has(mappedEvent.type)
      ) {
        this.websocketUpdate$.next();
      }
    });

    this.destroyRef.onDestroy(() => {
      this.websocketService.sendMessage(config.leaveEvent, config.tenantId);
    });
  }

  public totalSuppliersCount(): number {
    const suppliers = new Set(this.data().products.flatMap(p => p.suppliers || []));
    return suppliers.size;
  }

  public getSuppliersTooltipItems() {
    const suppliers = Array.from(new Set(this.data().products.flatMap(p => p.suppliers || [])));
    return suppliers.map((supplier, index) => ({
      id: `supplier-${index}`,
      text: supplier
    }));
  }

  public onRetry = output<ProductIntegrationRetryEvent>();

  public close(): void {
    this.dialogRef.close();
  }

  /** Produtos com falha: são os que o botão reenvia ao ERP. */
  protected readonly failedCount = computed(
    () => this.data().products.filter(p => p.status === ProductIntegrationStatus.INTEGRATION_FAILED).length
  );

  protected readonly retryLabel = computed(() => {
    const n = this.failedCount();
    return `Reenviar ${n} ${n === 1 ? 'produto' : 'produtos'} ao ERP`;
  });

  public tryAgain(): void {
    const failedProducts = this.data().products.filter(
      p => p.status === ProductIntegrationStatus.INTEGRATION_FAILED
    );

    const items: ApproveQuotationMapItem[] = failedProducts.map(p => ({
      offerId: p.offerId
    }));

    this.isLoading.set(true);

    this.onRetry.emit({
      purchaseId: this.data().purchaseId,
      items,
      setLoading: (value: boolean) => this.isLoading.set(value),
      updateData: (data: ProductIntegrationDialogData) => this.data.set(data)
    });
  }
}
